import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import './auth.css';

const RECOVERY_SESSION_KEY = 'rf_password_recovery_session';

function ResetPassword() {
  const navigate = useNavigate();
  const recoveryReadyRef = useRef(
    typeof window !== 'undefined' && sessionStorage.getItem(RECOVERY_SESSION_KEY) === '1'
  );

  const searchParams = new URLSearchParams(window.location.search);
  const tokenHashFromUrl = searchParams.get('token_hash');
  const typeFromUrl = searchParams.get('type');
  const codeFromUrl = searchParams.get('code');
  const isInviteFlow = typeFromUrl === 'invite' && !!tokenHashFromUrl;

  const [phase, setPhase] = useState(() => {
    if (isInviteFlow) return 'ready';
    return typeof window !== 'undefined' && sessionStorage.getItem(RECOVERY_SESSION_KEY) === '1'
      ? 'ready'
      : 'loading';
  });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isInvite] = useState(isInviteFlow);

  useEffect(() => {
    if (isInviteFlow) return undefined;

    let cancelled = false;

    const markReady = () => {
      if (cancelled) return;
      recoveryReadyRef.current = true;
      setPhase('ready');
    };

    if (sessionStorage.getItem(RECOVERY_SESSION_KEY) === '1') {
      sessionStorage.removeItem(RECOVERY_SESSION_KEY);
      markReady();
      return undefined;
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        sessionStorage.setItem(RECOVERY_SESSION_KEY, '1');
        markReady();
      }
    });

    const verifyFromUrl = async () => {
      await new Promise((r) => setTimeout(r, 0));

      // PKCE flow: exchange the code for a session
      if (codeFromUrl) {
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(codeFromUrl);
        if (!exchangeError && !cancelled) {
          markReady();
          return;
        }
      }

      // Hash/token_hash flow fallback
      const { data: { session } } = await supabase.auth.getSession();
      if (!cancelled && session) {
        markReady();
      }
    };
    verifyFromUrl();

    const invalidTimer = setTimeout(() => {
      if (!cancelled && !recoveryReadyRef.current) {
        setPhase('invalid');
      }
    }, 8000);

    return () => {
      cancelled = true;
      clearTimeout(invalidTimer);
      subscription.unsubscribe();
    };
  }, [isInviteFlow, codeFromUrl]);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => {
      navigate(isInvite ? '/onboarding' : '/', { replace: true });
    }, 2000);
    return () => clearTimeout(t);
  }, [success, navigate, isInvite]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setSubmitting(true);

    if (isInvite && tokenHashFromUrl) {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHashFromUrl,
        type: 'invite',
      });

      if (verifyError) {
        setSubmitting(false);
        setError('This link has expired or already been used. Please request a new one.');
        return;
      }
    }

    const { error: updateError } = await supabase.auth.updateUser({ password });

    setSubmitting(false);

    if (updateError) {
      setError(updateError.message || 'Could not update password. Please try again.');
      return;
    }

    setSuccess(true);
  };

  if (phase === 'loading') {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 className="auth-title">Reset password</h1>
          <p className="auth-subtitle">Verifying your link…</p>
        </div>
      </div>
    );
  }

  if (phase === 'invalid') {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 className="auth-title">Link invalid or expired</h1>
          <p className="auth-subtitle">
            Request a new link from the sign-in page, then open it from your email.
          </p>
          <Link to="/" className="auth-inline-link">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 className="auth-title">Password set!</h1>
          <p className="auth-subtitle">
            {isInvite ? 'Taking you to setup…' : 'Redirecting you to sign in…'}
          </p>
          <div className="auth-message auth-message-success">
            {isInvite
              ? 'Your account is ready. Setting up your ReplyFirst now.'
              : 'Your password has been changed. You can sign in with your new password.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-title">{isInvite ? 'Create your password' : 'Set a new password'}</h1>
        <p className="auth-subtitle">
          {isInvite ? 'Choose a password to access your ReplyFirst dashboard' : 'Choose a password for your account'}
        </p>
        <form className="auth-form" onSubmit={handleSubmit}>
          <label className="auth-label">
            {isInvite ? 'Password' : 'New password'}
            <input
              type="password"
              className="auth-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              required
              minLength={6}
            />
          </label>
          <label className="auth-label">
            Confirm password
            <input
              type="password"
              className="auth-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              required
              minLength={6}
            />
          </label>
          {error && <div className="auth-message auth-message-error">{error}</div>}
          <button type="submit" className="auth-button" disabled={submitting}>
            {submitting ? 'Setting up…' : isInvite ? 'Create password & continue →' : 'Update password'}
          </button>
        </form>
        <Link to="/" className="auth-inline-link">
          Back to sign in
        </Link>
      </div>
    </div>
  );
}

export default ResetPassword;